import { useState } from 'react';

import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';
import { cn } from '@poseidon/components/cn';
import { ButtonLink } from '@poseidon/components/Input2';
import { Editor } from '@poseidon/components/StrokeIcons';

import type { SequenceGraphHistory } from '~/components/Sequence/SequenceGraphEditor';
import { SequenceCanvasHeader } from '~/components/Sequence/SequenceCanvasHeader';
import { SequenceGraphEditor } from '~/components/Sequence/SequenceGraphEditor';
import { CANVAS_CONTROL_BAR_CLASS } from '~/components/Sequence/SequencePanZoom';
import { useContactSequenceEnrollments } from '~/components/Sequence/useContactSequenceEnrollments';

interface Props {
  teamId: string;
  /** The open contact's public id. */
  contactId: string;
  /**
   * The contact's history (oldest first), what each entry can do, and paging it further back — drawn at the
   * top of the canvas. The contact page owns the paging (`useOlderContactHistory`), so it outlives this view.
   */
  history: SequenceGraphHistory;
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
}

/**
 * The interact tab's GRAPH view: the whole tab as ONE canvas — the contact's history flowing down into the
 * locked graph of their sequence. It fills the section exactly (no page scroll around it), so the wheel /
 * trackpad pans it and nothing else scrolls; it opens at 100% on where they are now. A contact in more than one
 * sequence picks which one at the top-left, where a paused one also says so; "Edit sequence" at the bottom-left
 * opens the one on screen in its builder.
 */
export function ContactSequenceCanvas({ teamId, contactId, history, variables, documents }: Props) {
  const { enrollments, saveStepContent, completeManualStep } = useContactSequenceEnrollments(teamId, contactId);
  const [pickedSequenceId, setPickedSequenceId] = useState<string | null>(null);
  const enrollment = enrollments.find((e) => e.sequencePublicId === pickedSequenceId) ?? enrollments[0];
  if (enrollment == null) return null;
  return (
    <div className="relative size-full">
      <SequenceCanvasHeader
        sequences={enrollments.map((e) => ({ id: e.sequencePublicId, name: e.sequenceName }))}
        selectedId={enrollment.sequencePublicId}
        onSelect={setPickedSequenceId}
        paused={enrollment.pausedAt != null}
      />
      <SequenceGraphEditor
        // A fresh canvas per sequence: its opening frame (on "now") belongs to the graph it opened on.
        key={enrollment.enrollmentId}
        readOnly
        fillsSection
        teamId={teamId}
        sequenceId={enrollment.sequencePublicId}
        initialSteps={enrollment.steps}
        overrides={enrollment.overrides}
        stepStatus={enrollment.stepStatus}
        currentNodeId={enrollment.currentNodeId}
        onSaveStepContent={saveStepContent(enrollment)}
        onCompleteManualStep={completeManualStep(enrollment)}
        variables={variables}
        documents={documents}
        history={history}
      />
      {/* The way into this sequence's builder to change its steps (this canvas only edits one contact's copy of
          the text) — bottom-left, a control bar like the zoom controls across the bottom-right. */}
      <div className={cn('absolute bottom-3 left-3', CANVAS_CONTROL_BAR_CLASS)}>
        <ButtonLink
          data-edit-sequence
          size="xs"
          text
          icon={<Editor.Pencil03 />}
          href={`/sequence/${enrollment.sequencePublicId}`}
          tooltip={`Open “${enrollment.sequenceName}” to edit its steps`}>
          Edit sequence
        </ButtonLink>
      </div>
    </div>
  );
}
